/**
 * The email provider interface.
 *
 * Deliverability is the make-or-break dependency of this whole system: "If verification codes do
 * not arrive, applicants cannot apply" (docs/06-DEPLOYMENT-AND-OPS.md). Zoho is the configured
 * provider because of the paid-org constraint, but it is reached through this interface precisely
 * so that swapping it is one environment variable and a restart — the runbook's answer to "Zoho
 * throttles at 4pm on deadline day" depends on that being true.
 *
 * Lives in packages/shared rather than apps/api because both the API (test sends, mail:doctor) and
 * the worker (the actual queue consumer) need it.
 */

export interface EmailMessage {
  to: string;
  subject: string;
  html: string;
  text: string;
  /** Overrides the configured default. Used by test sends so replies reach the right person. */
  replyTo?: string;
  /**
   * A visible carbon copy. Used by an individual broadcast resend, so that sending somebody their
   * message again leaves evidence in a coordinator's own inbox rather than only in a table.
   *
   * Visible on purpose: a Bcc would hide from the recipient that a third party holds a copy of
   * their message, and for something that may carry test credentials that is the wrong default.
   */
  cc?: string;
}

export interface EmailSendResult {
  /** Provider's own id, recorded in notification_log so a delivery can be traced with support. */
  providerId: string | null;
  accepted: boolean;
  /**
   * Which relay actually accepted it. With a failover configured this is the difference between
   * "the primary is fine" and "everything has been going out through the backup since Tuesday" —
   * a distinction nobody can make after the fact if it is not written down.
   */
  provider?: string;
}

export interface EmailProvider {
  readonly name: string;
  send(message: EmailMessage): Promise<EmailSendResult>;
  /** Proves the credentials work without sending anything. Used by `pnpm mail:doctor`. */
  verify(): Promise<{ ok: boolean; error?: string }>;
}

/**
 * The relays that can appear in the chain, by the names `notification_log.provider` already stores.
 * `smtp` is whatever `SMTP_HOST` points at — or the Resend stub when `EMAIL_PROVIDER=resend`.
 *
 * Declared here rather than beside `createEmailProvider` because `env.ts` validates
 * `EMAIL_RELAY_ORDER` against it, and `env.ts` is re-exported from the package barrel that the
 * browser bundles import. Importing it from `smtp.js` would drag Nodemailer into apps/public.
 */
export const RELAY_NAMES = ["smtp", "smtp-fallback", "mailgun"] as const;
export type RelayName = (typeof RELAY_NAMES)[number];

/**
 * The DNS records the sending domain must publish. Printed by `mail:doctor` and reproduced here
 * from docs/06-DEPLOYMENT-AND-OPS.md so the script and the runbook cannot drift apart.
 */
export const requiredDnsRecords = (
  domain: string,
  /**
   * Which relays actually send. SPF has to authorise every one of them, not just the first: each
   * sends from its own infrastructure, and a relay the domain does not name fails SPF at the
   * receiver even though the relay itself accepted the message. Printing only Zoho's include while
   * Mailgun is the relay doing the sending is worse than printing nothing.
   */
  relays: { zoho?: boolean; mailgun?: boolean } = { zoho: true },
): { name: string; type: string; value: string; note: string }[] => [
  {
    name: "@",
    type: "TXT",
    value: `v=spf1${relays.zoho === false ? "" : " include:zohomail.com"}${
      relays.mailgun ? " include:mailgun.org" : ""
    } ~all`,
    note: "Merge with any existing SPF record — a domain may publish only ONE. Two SPF records is a hard fail, not a warning. Zoho's own include is zohomail.com; the older zoho.com still resolves but is not what the console issues. Every relay in EMAIL_RELAY_ORDER needs its include here.",
  },
  ...(relays.mailgun
    ? [
        {
          name: "<selector>._domainkey  (Mailgun issues one per domain)",
          type: "TXT",
          value: "(copied from the Mailgun domain settings page)",
          note: "Separate from Zoho's key — a domain publishes one DKIM record per sending relay, under different selectors. Mailgun's domain page shows the exact selector and value; it stays 'Unverified' there until the record resolves.",
        },
      ]
    : []),
  {
    // The selector is chosen by Zoho, not by us, and it is NOT "zoho": current consoles issue
    // "zmail". Naming the wrong selector here sends an operator hunting for a record that was
    // never going to exist, and concluding DKIM is unconfigured when it is published and valid.
    name: "<selector>._domainkey  (Zoho currently issues: zmail)",
    type: "TXT",
    value: "(generated in the Zoho admin console)",
    note: 'Read the exact selector off the Zoho DKIM page — do not assume it. Publish it, then confirm it shows "Verified" there. An unverified DKIM key is the same as none.',
  },
  {
    name: "_dmarc",
    type: "TXT",
    value: `v=DMARC1; p=none; rua=mailto:dmarc@${domain}`,
    note: "Start at p=none, review the aggregate reports for two weeks, then move to p=quarantine. Going straight to quarantine risks losing real mail.",
  },
];
